import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import '../styles/legal.css';

const PRIVACY_SECTIONS = [
  {
    title: 'Who We Are',
    body: [
      'LaborConnect is operated by LaborConnect, Philippines (“LaborConnect,” “we,” “us”).',
      'LaborConnect acts as the Personal Information Controller for personal data processed through LaborConnect, except where a specific processing arrangement states otherwise.',
    ],
  },
  {
    title: 'Scope',
    body: [
      'This Policy applies to personal data collected and processed through LaborConnect, including registration, profile management, document uploads, digital ID/QR verification, venue booking, and support ticketing.',
    ],
  },
  {
    title: 'Information We Collect',
    body: [
      '3.1 Personal and Membership Information: Full name, contact details, date of birth/sex (as applicable), civil status (if required), and membership details; company/employment information and union role; dependent-related information (e.g., number of children and related dependent data where required).',
      '3.2 Sensitive Personal Information (when required): Government or organization ID images; digital signatures; supporting documents submitted for ticket handling or membership verification; other data classified as sensitive under applicable law, if required by process.',
      '3.3 Ticket and Communication Data: Ticket subject, description, attachments, messages, timestamps, assigned department/handler, and status history.',
      '3.4 Digital ID/QR Verification Data: QR reference data, scan logs (date/time, scanner identity/role where applicable), and membership validity checks.',
      '3.5 Venue Booking Data: Requested venue, date/time, purpose (if required), request/approval records.',
      '3.6 Technical and Security Data: Device/browser/app logs, IP address, session logs, audit trails, and security events for fraud prevention and system integrity.',
    ],
  },
  {
    title: 'How We Use Information',
    body: [
      'We process personal data for legitimate LaborConnect purposes, including: membership registration, validation, and account administration; document verification and compliance checks; generation and verification of Digital ID and QR credentials; processing venue booking requests and approvals; receiving, routing, and resolving support tickets; improving service quality, analytics, and operations; security monitoring, fraud detection, incident response, and audit; and compliance with legal/regulatory obligations and lawful orders.',
    ],
  },
  {
    title: 'AI Processing for Ticket Summaries',
    body: [
      '5.1 LaborConnect may use AI tools to process ticket text and generate concise summaries for administrators.',
      '5.2 Purpose: Improve routing efficiency, prioritization, and response workflow.',
      '5.3 Safeguards and limits: AI outputs are reviewed by authorized personnel; AI summaries are support aids and not final legal/medical conclusions; AI processing is subject to access controls, confidentiality, and governance measures.',
    ],
  },
  {
    title: 'Legal Bases for Processing',
    body: [
      'Depending on context, LaborConnect may process personal data based on: your consent; performance of a contract or membership-related service; compliance with legal obligations; protection of lawful rights and legitimate interests of LaborConnect and members; and other lawful criteria recognized under Philippine data privacy law.',
      'For sensitive personal information, LaborConnect applies stricter protection and relies on valid lawful bases permitted by law.',
    ],
  },
  {
    title: 'Data Sharing and Disclosure',
    body: [
      'LaborConnect may share personal data only when necessary and lawful, such as with authorized LaborConnect officers, staff, and departments on a need-to-know basis; service providers (e.g., hosting, security, communications, support systems) under data protection obligations; and government/regulatory authorities when required by law, lawful order, or legal process.',
      'LaborConnect does not sell personal data.',
    ],
  },
  {
    title: 'Cross-Border Processing',
    body: [
      'Where systems or service providers involve processing outside the Philippines, LaborConnect ensures appropriate safeguards (contractual, organizational, and technical) consistent with Philippine data privacy requirements.',
    ],
  },
  {
    title: 'Data Retention',
    body: [
      'LaborConnect retains personal data only as long as necessary for the declared purposes, operational needs, dispute handling, audit, and legal/regulatory compliance.',
      'Indicative retention approach: Active account and membership records are retained during active membership and for a lawful post-membership retention period; ticket and support records are retained based on case handling, audit, and legal requirements; security/audit logs are retained per cybersecurity and compliance needs; records subject to legal hold, investigation, or claims are retained until closure and required post-closure period.',
      'After retention periods, data is securely disposed of, anonymized, or de-identified when feasible.',
    ],
  },
  {
    title: 'Data Security Measures',
    body: [
      'LaborConnect uses reasonable and appropriate organizational, physical, and technical security controls, such as role-based access controls and least-privilege permissions; authentication controls, password safeguards, and session protections; encryption or equivalent safeguards in transit and at rest where appropriate; logging, monitoring, and incident management; and staff confidentiality obligations and privacy/security awareness.',
      'No system is absolutely secure, but LaborConnect continuously improves safeguards.',
    ],
  },
  {
    title: 'Data Breach Management',
    body: [
      'In the event of a personal data breach requiring notification under applicable law and NPC rules, LaborConnect will take appropriate action, including timely notification to affected data subjects and/or the NPC when required.',
    ],
  },
  {
    title: 'Your Rights Under RA 10173',
    body: [
      'Subject to legal limits and verification of identity, you may exercise: right to be informed; right of access; right to object; right to correction/rectification; right to erasure or blocking; right to data portability (where applicable); right to damages; and right to lodge a complaint with the National Privacy Commission.',
      'Where processing is based on consent, you may withdraw consent, subject to legal and contractual limitations and the lawfulness of prior processing.',
    ],
  },
  {
    title: 'How to Exercise Your Privacy Rights',
    body: [
      'Send requests to LaborConnect’s Data Protection team with sufficient details for identity verification and request handling.',
      'Data Protection Officer (DPO): [Insert Name/Office] | Email: [Insert DPO Email] | Phone: [Insert Phone] | Address: [Insert Office Address]',
      'LaborConnect may request supporting information to verify identity and evaluate lawful scope of requests.',
    ],
  },
  {
    title: 'Children and Dependent Data',
    body: [
      'If you submit data about dependents/minors, you represent that you are authorized to provide such information and that submission is lawful and necessary for declared LaborConnect purposes.',
    ],
  },
  {
    title: 'Cookies and Similar Technologies',
    body: [
      'Where applicable, LaborConnect may use essential cookies/session technologies for login, security, and functionality. Additional analytics or non-essential technologies, if used, will be managed in accordance with notice/consent requirements.',
    ],
  },
  {
    title: 'Changes to this Privacy Policy',
    body: [
      'LaborConnect may update this Policy from time to time. Material changes will be posted in LaborConnect with a revised effective date.',
    ],
  },
  {
    title: 'Contact and Complaints',
    body: [
      'For privacy concerns, contact LaborConnect’s DPO using the details above. You may also contact the National Privacy Commission through its official channels for unresolved concerns.',
    ],
  },
];

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    const hasStoredAuth = Boolean(
      localStorage.getItem('alu_user_auth') ||
      sessionStorage.getItem('alu_user_auth_session') ||
      localStorage.getItem('alu_user') ||
      localStorage.getItem('alu_token')
    );

    navigate(hasStoredAuth ? '/dashboard' : '/');
  };

  return (
    <main className="legal-page">
      <div className="legal-page__container">
        <button type="button" className="legal-page__back-link" onClick={handleBack}>
          <ArrowLeft size={16} />
          Back to LaborConnect
        </button>

        <header className="legal-page__header">
          <div className="legal-page__icon">
            <ShieldCheck size={24} />
          </div>
          <div>
            <h1>LaborConnect Data Privacy Policy</h1>
            <p>
              Effective Date: Dec 2026 | Last Updated: Mar 2026
            </p>
          </div>
        </header>

        <section className="legal-page__notice">
          <strong>Legal Basis:</strong> This policy is aligned with the Data Privacy Act of 2012 (Republic Act No. 10173), its Implementing Rules and Regulations, and applicable issuances of the National Privacy Commission (NPC).
        </section>

        <div className="legal-page__content">
          {PRIVACY_SECTIONS.map((section) => (
            <section key={section.title} className="legal-page__section">
              <h2>{section.title}</h2>
              {section.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}
        </div>

        <footer className="legal-page__footer">
          See also: <Link to="/terms-and-conditions" className="legal-page__inline-link">LaborConnect Terms &amp; Conditions</Link>
        </footer>
      </div>
    </main>
  );
}
